import { Checkbox, Col, InputNumber, Radio, Select, Typography } from 'antd';
import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { useConfigurationStepOptions } from 'src/features/programsV2/hooks/useConfigurationStepOptions';
import { ConfigurationStepForm } from 'src/features/programsV2/schemas/configurationStepSchema';

import { ProgramFormItem } from '../../ProgramFormItem';
import { ProgramSectionGroup } from '../../ui/ProgramSectionGroup';

const { Text } = Typography;

export const ConfigurationForm: FC = () => {
  const { configurationStepOptions, loadingOptions } =
    useConfigurationStepOptions();

  const { control, watch } = useFormContext<ConfigurationStepForm>();

  const totalDurationInDays = watch('totalDurationInDays');

  const durationOptions = useMemo(() => {
    const isPlural = totalDurationInDays && totalDurationInDays > 1;
    return [
      { value: 1, label: `${isPlural ? 'Dias' : 'Dia'}` },
      { value: 7, label: `${isPlural ? 'Semanas' : 'Semana'}` },
      { value: 30, label: `${isPlural ? 'Meses' : 'Mês'}` },
    ];
  }, [totalDurationInDays]);

  return (
    <Section>
      <ProgramSectionGroup isHeader>
        <Text>Configurações sistêmicas</Text>
      </ProgramSectionGroup>

      <ProgramSectionGroup gutter={[16, 16]}>
        <Col lg={6} xl={6}>
          <ProgramFormItem
            control={control}
            name='averageContentTimeInMinutes'
            label='Tempo médio da aula (em min)'
            renderItem={({ field }) => (
              <InputNumber
                {...field}
                type='number'
                style={{ width: '100%' }}
                min={0}
                precision={0}
                onChange={value => field.onChange(value ?? 0)}
                placeholder='Ex: 30min (apenas valores numéricos)'
              />
            )}
          />
        </Col>

        <Col lg={6} xl={5} style={{ paddingRight: '0px' }}>
          <ProgramFormItem
            control={control}
            name='totalDurationInDays'
            label='Duração total do programa'
            renderItem={({ field }) => (
              <InputNumber
                {...field}
                type='number'
                style={{ width: '100%' }}
                min={0}
                precision={0}
                onChange={value => field.onChange(value ?? 0)}
                placeholder='Ex.: 4'
              />
            )}
          />
        </Col>
        <Col span={3}>
          <ProgramFormItem
            control={control}
            name='durationMeasure'
            label=''
            defaultValue={durationOptions[0].value}
            renderItem={({ field }) => (
              <Select
                {...field}
                options={durationOptions}
                style={{ width: '100%', marginTop: '26px' }}
              />
            )}
          />
        </Col>

        <Col lg={9} xl={10}>
          <ProgramFormItem
            control={control}
            name='numberOfClasses'
            label='Volume total de aulas do programa'
            renderItem={({ field }) => (
              <InputNumber
                {...field}
                onChange={field.onChange}
                value={field.value ? field.value : undefined}
                placeholder='Valor automático conforme conteúdos cadastrados.'
                type='number'
                min={1}
                style={{ width: '100%' }}
                disabled
              />
            )}
          />
        </Col>
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <Col span={24}>
          <ProgramFormItem
            control={control}
            name='objectiveId'
            label='Objetivo primário'
            isLoading={loadingOptions}
            loadingType='options'
            renderItem={({ field }) => (
              <Radio.Group
                {...field}
                optionType='button'
                options={configurationStepOptions.objectives}
                style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
                onChange={event => {
                  const newValue = event.target.value;
                  field.onChange(newValue);
                }}
              />
            )}
          />
        </Col>
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <Col span={16}>
          <ProgramFormItem
            control={control}
            name='categoryId'
            label='Categoria (Somente para TV)'
            isLoading={loadingOptions}
            loadingType='options'
            renderItem={({ field }) => (
              <Radio.Group
                {...field}
                options={configurationStepOptions.categories}
                optionType='button'
              />
            )}
          />
        </Col>
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          name='levelId'
          label='Nível'
          isLoading={loadingOptions}
          loadingType='options'
          renderItem={({ field }) => (
            <Radio.Group
              {...field}
              options={configurationStepOptions.levels}
              optionType='button'
            />
          )}
        />
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          name='modalityIds'
          label='Modalidades'
          isLoading={loadingOptions}
          loadingType='options'
          renderItem={({ field }) => (
            <Checkbox.Group
              {...field}
              options={configurationStepOptions.modalities}
            />
          )}
        />
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          name='tags'
          label='Tags'
          renderItem={({ field }) => (
            <Select
              mode='tags'
              showSearch
              tokenSeparators={[',']}
              {...field}
              placeholder='Ex.: Funcional, Lançamento'
              onChange={(values: string[]) => {
                const trimmedValues = values.map(v => v.trim()).filter(Boolean);
                field.onChange(trimmedValues);
              }}
              options={configurationStepOptions.tags}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          )}
        />
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          name='accessory'
          label='Uso de acessórios'
          renderItem={({ field }) => (
            <Radio.Group
              {...field}
              optionType='button'
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
              options={configurationStepOptions.accessory}
            />
          )}
        />
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          name='gender'
          label='Gênero'
          renderItem={({ field }) => (
            <Checkbox.Group
              {...field}
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
              options={configurationStepOptions.gender}
            />
          )}
        />
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          name='forMothers'
          label='Para mães'
          isLoading={loadingOptions}
          loadingType='options'
          renderItem={({ field }) => (
            <Radio.Group
              {...field}
              optionType='button'
              options={configurationStepOptions.forMothers}
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
            />
          )}
        />
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          name='durationValue'
          label='Duração de programas'
          isLoading={loadingOptions}
          loadingType='options'
          renderItem={({ field }) => (
            <Radio.Group
              {...field}
              optionType='button'
              options={configurationStepOptions.durationProgram}
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
            />
          )}
        />
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          name='isRecommendQuiz'
          label='Recomendado no Quiz'
          renderItem={({ field }) => (
            <Radio.Group
              {...field}
              optionType='button'
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
              options={configurationStepOptions.isRecommendQuiz}
            />
          )}
        />
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          name='trainingDaysAvailable'
          label='Disponibilidade para treinar (Dias por semana)'
          renderItem={({ field }) => (
            <InputNumber
              {...field}
              placeholder='Ex.: 1 (apenas valores numéricos)'
              precision={0}
              type='number'
              style={{ width: '100%' }}
              onChange={value => field.onChange(value ?? 0)}
            />
          )}
        />
      </ProgramSectionGroup>

      <ProgramSectionGroup>
        <ProgramFormItem
          control={control}
          label='Listar na busca'
          name='showInSearch'
          renderItem={({ field }) => (
            <Radio.Group
              {...field}
              optionType='button'
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
              options={configurationStepOptions.showInSearch}
            />
          )}
        />
      </ProgramSectionGroup>
    </Section>
  );
};

const Section = styled.div`
  border-radius: 8px;
  border: 1px solid #303030;

  .ant-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .ant-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }

  .ant-checkbox-wrapper {
    height: 32px;
    border: 1px solid #424242;
    cursor: pointer;
    transition:
      color 0.3s,
      background 0.3s,
      border-color 0.3s,
      box-shadow 0.3s;
    margin: 0;

    &:hover {
      color: #177ddc;
    }

    .ant-checkbox {
      display: none;
    }

    span {
      padding: 0 15px;
      line-height: 30px;
    }
  }

  .ant-checkbox-wrapper-checked:not(.ant-checkbox-wrapper-disabled) {
    color: #177ddc;
    border-color: #177ddc;

    &:hover {
      color: #165996;
      border-color: #165996;
    }
  }

  .ant-select,
  .ant-input-number,
  .ant-input,
  .ant-radio-group,
  .ant-checkbox-group,
  .ant-picker {
    width: 100%;
  }

  .ant-select,
  .ant-input-number,
  .ant-input,
  .ant-radio-button-wrapper,
  .ant-checkbox-wrapper,
  .ant-picker {
    background-color: #141414;
  }

  /* Fix for Google Chrome Autofill colors */
  .ant-input-number,
  .ant-input {
    transition:
      color calc(infinity * 1s) step-end,
      background-color calc(infinity * 1s) step-end;
  }
`;
